// Verificación en dos pasos (TOTP) del HCE — llamadas a la bóveda (MT-043-B/C). Tres familias de credencial, TODAS sin que el navegador toque un
// secreto de Medplum:
//   - TICKET (aún sin sesión): lo entrega `POST /api/hce/medplum/auth/login` cuando la cuenta clínica necesita el factor; la bóveda retuvo el `code`
//     de Medplum y solo lo libera con el código del teléfono (o de respaldo) o al terminar el enrolamiento.
//   - TOKEN DE ENROLAMIENTO del administrador (lo entrega `POST /api/hce/admin-login`, 15 min).
//   - SESIÓN (Bearer opaco de la sesión de proxy, o token del administrador): activación voluntaria, estado y códigos de respaldo nuevos.
// Nada de lo que devuelven estas funciones se guarda: el secreto, el QR y los códigos de respaldo viven solo en el estado de la pantalla.
import { cabecerasBoveda } from './audit/auditedFetch';
import { vaultUrl } from './hceVault';

/** `codigo` es la marca estable del servidor (p. ej. `ticket-vencido`): la pantalla decide con ella, no con el texto del mensaje. */
export type ErrorMfa = { ok: false; error: string; status: number; codigo?: string };

export const CODIGO_TICKET_VENCIDO = 'ticket-vencido';

export type DatosEnrolamiento = { secreto: string; uri: string; qr: string };
export type CodigosRespaldo = { backupCodes: string[]; backupCodesPdf: string };

export type EstadoMfa = {
  activo: boolean;
  activoDesde: string | null;
  respaldoRestantes: number;
  politica: { obligatorio: boolean; desde: string | null; origen: 'entorno' | 'consultorio' | 'ninguna'; invalida: boolean };
};

const ERROR_GENERICO = 'No se pudo completar la operación. Intentá de nuevo.';

async function llamar<T>(metodo: 'GET' | 'POST', ruta: string, cuerpo: unknown, cabeceras: Record<string, string> = {}): Promise<({ ok: true } & T) | ErrorMfa> {
  try {
    const res = await fetch(vaultUrl(ruta), {
      method: metodo,
      headers: { ...(cuerpo !== undefined ? { 'content-type': 'application/json' } : {}), ...cabeceras },
      ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
    });
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) return { ok: false, status: res.status, error: typeof json.error === 'string' && json.error ? json.error : ERROR_GENERICO, ...(typeof json.codigo === 'string' ? { codigo: json.codigo } : {}) };
    return { ok: true, ...(json as T) };
  } catch {
    return { ok: false, status: 0, error: 'No se pudo conectar. Revisá tu conexión e intentá de nuevo.' };
  }
}

// --- Personal clínico: con ticket -------------------------------------------------------------------------------------------------------

/** Código del teléfono (o de respaldo) -> el `code` de Medplum, que se canjea con `medplum.processCode`. */
export function verificarConTicket(ticket: string, code: string) {
  return llamar<{ code: string }>('POST', '/api/hce/mfa/verificar', { ticket, code });
}
export function enrolarIniciarConTicket(ticket: string) {
  return llamar<DatosEnrolamiento>('POST', '/api/hce/mfa/enrolar-iniciar', { ticket });
}
/** `code` es nulo si el ticket se agotó entre medias: el factor ya quedó activo y hay que iniciar sesión de nuevo (los códigos de respaldo SÍ llegan). */
export function enrolarConfirmarConTicket(ticket: string, code: string) {
  return llamar<CodigosRespaldo & { code: string | null }>('POST', '/api/hce/mfa/enrolar-confirmar', { ticket, code });
}

// --- Administrador del HCE: con el token de enrolamiento ----------------------------------------------------------------------------------

export function enrolarIniciarAdmin(enrollToken: string) {
  return llamar<DatosEnrolamiento>('POST', '/api/hce/admin-mfa/enrolar-iniciar', { enrollToken });
}
export function enrolarConfirmarAdmin(enrollToken: string, code: string) {
  return llamar<CodigosRespaldo & { token: string; name: string }>('POST', '/api/hce/admin-mfa/enrolar-confirmar', { enrollToken, code });
}

// --- Con sesión -----------------------------------------------------------------------------------------------------------------------------

/** Personal clínico: la sesión es el Bearer opaco del proxy (`cabecerasBoveda`). */
export const sesionClinica = {
  estado: () => llamar<EstadoMfa>('GET', '/api/hce/mfa/estado', undefined, cabecerasBoveda()),
  /** Activar el factor exige la contraseña además de la sesión (el servidor la comprueba contra Medplum): una sesión robada no activa SU factor. */
  activarIniciar: (password: string) => llamar<DatosEnrolamiento>('POST', '/api/hce/mfa/activar-iniciar', { password }, cabecerasBoveda()),
  activarConfirmar: (code: string, password: string) => llamar<CodigosRespaldo>('POST', '/api/hce/mfa/activar-confirmar', { code, password }, cabecerasBoveda()),
  regenerarRespaldo: (code: string) => llamar<CodigosRespaldo>('POST', '/api/hce/mfa/respaldo-regenerar', { code }, cabecerasBoveda()),
};

/** Administrador del HCE: la sesión es su token propio. */
export function sesionAdmin(token: string) {
  const cab = { authorization: `Bearer ${token}` };
  return {
    estado: () => llamar<EstadoMfa>('GET', '/api/hce/admin-mfa/estado', undefined, cab),
    regenerarRespaldo: (code: string) => llamar<CodigosRespaldo>('POST', '/api/hce/admin-mfa/respaldo-regenerar', { code }, cab),
  };
}

// --- Administración (MT-043-C): reinicio, desbloqueo e informe de cobertura -------------------------------------------------------------

export function reiniciarMfaDeCuenta(token: string, id: number) {
  return llamar<{ cambio: boolean }>('POST', `/api/hce/usuarios/${id}/mfa-reiniciar`, {}, { authorization: `Bearer ${token}` });
}
export function desbloquearMfaDeCuenta(token: string, id: number) {
  return llamar<{ cambio: boolean }>('POST', `/api/hce/usuarios/${id}/mfa-desbloquear`, {}, { authorization: `Bearer ${token}` });
}

export type CoberturaPerfil = {
  rol: 'admin' | 'personal-medico' | 'director-tecnico-farmacia';
  etiqueta: string;
  activos: number;
  conFactor: number;
  sinFactor: number;
  cobertura: number | null;
  obligatorio: boolean;
  alcanzadoPorLaPolitica: boolean;
  desde: string | null;
};
export type InformeCobertura = {
  generadoEl: string;
  politica: { origen: 'entorno' | 'consultorio' | 'ninguna'; desde: string | null; invalida: boolean; perfiles: string[]; obligatorioAhora: boolean };
  perfiles: CoberturaPerfil[];
  total: { activos: number; conFactor: number; sinFactor: number; cobertura: number | null };
  /** Correos enmascarados (`d***@dominio`); el completo solo viaja en el CSV. */
  pendientes: { id: number; usuario: string; nombre: string; rol: string }[];
  pendientesTruncado?: boolean;
};

export function obtenerCobertura(token: string) {
  return llamar<InformeCobertura>('GET', '/api/hce/mfa-cobertura', undefined, { authorization: `Bearer ${token}` });
}

/** El CSV del informe (queda anotado en la bitácora del consultorio). Devuelve el texto para que la pantalla lo descargue. */
export async function descargarCoberturaCsv(token: string): Promise<{ ok: true; csv: string; nombre: string } | ErrorMfa> {
  try {
    const res = await fetch(vaultUrl('/api/hce/mfa-cobertura?formato=csv'), { headers: { authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      return { ok: false, status: res.status, error: json.error || ERROR_GENERICO };
    }
    const nombre = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'cobertura-verificacion-dos-pasos.csv';
    return { ok: true, csv: await res.text(), nombre };
  } catch {
    return { ok: false, status: 0, error: 'No se pudo conectar. Revisá tu conexión e intentá de nuevo.' };
  }
}

// --- Utilidades de pantalla ---------------------------------------------------------------------------------------------------------------

/** Qué hacer con la respuesta de `auth/login` de Medplum (ya pasada por la bóveda). Función pura para poder probarla sin pantalla. */
export type PasoLoginClinico =
  | { paso: 'sesion'; code: string }
  | { paso: 'codigo'; ticket: string }
  | { paso: 'enrolar'; ticket: string }
  | { paso: 'multiples-clinicas' }
  | { paso: 'error' };

export function pasoDeLoginClinico(resp: { code?: string; mfaRequired?: boolean; mfaEnrollRequired?: boolean; mfaTicket?: string; memberships?: unknown[] }): PasoLoginClinico {
  // El ticket es lo que manda: si la bóveda retuvo el `code`, no hay `code` aquí.
  if (resp.mfaTicket && resp.mfaEnrollRequired) return { paso: 'enrolar', ticket: resp.mfaTicket };
  if (resp.mfaTicket && resp.mfaRequired) return { paso: 'codigo', ticket: resp.mfaTicket };
  // Un `mfaRequired` SIN ticket es el MFA nativo de Medplum, que este acceso no usa (no está activado en ninguna cuenta del HCE).
  if (resp.mfaRequired || resp.mfaEnrollRequired) return { paso: 'error' };
  if (resp.memberships && resp.memberships.length > 1) return { paso: 'multiples-clinicas' };
  if (resp.code) return { paso: 'sesion', code: resp.code };
  return { paso: 'error' };
}

/** El PDF de los códigos de respaldo (base64) como Blob descargable. */
export function pdfDeBase64(base64: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
}

/** Descarga un Blob con el nombre dado (sin dejar el objeto URL vivo). */
export function descargarBlob(blob: Blob, nombre: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
